
import { useMediaQuery } from '@material-ui/core';
import React from 'react';

import { translate } from '../../../base/i18n';
import { Icon, IconCheck, IconExclamation } from '../../../base/icons';
import { connect } from '../../../base/redux';
import {
    getDeviceStatusText,
    getDeviceStatusType,
    getRawError
} from '../../functions';
declare var interfaceConfig: Object;

export type Props = {

    /**
     * The text to be displayed in relation to the status of the audio/video devices.
     */
    deviceStatusText: string,

    /**
     * The type of status for current devices, controlling the background color of the text.
     * Can be `ok` or `warning`.
     */
    deviceStatusType: string,

    /**
     * The error coming from device configuration.
     */
    rawError: string,

    /**
     * Used for translation.
     */
    t: Function
};

const iconMap = {
    warning: {
        src: IconExclamation,
        className: 'prejoin-preview-status--warning'
    },
    ok: {
        src: IconCheck,
        className: 'prejoin-preview-status--ok',
        text: 'prejoin.lookGood'
    }
};


/**
 * Strip showing the current status of the devices.
 * User is informed if there are missing or malfunctioning devices.
 *
 * @returns {ReactElement}
 */
function DeviceStatus({ deviceStatusType, deviceStatusText, t, rawError }: Props) {
    const [ micPermission, setMicPermission ] = React.useState(null);
    const [ camPermission, setCamPermission ] = React.useState(null);
    const matched = useMediaQuery('(max-width:760px)');

    React.useEffect(() => {
        const permissionsApi = navigator?.permissions;

        if (typeof permissionsApi?.query !== 'function') {
            return undefined;
        }

        let camPerm;
        let micPerm;

        permissionsApi.query({ name: 'microphone' })
            .then(status => {
                setMicPermission(status.state);
                micPerm = status;
                status.onchange = () => setMicPermission(status.state);
            })
            .catch(() => {
                setMicPermission(null);
            });

        permissionsApi.query({ name: 'camera' })
            .then(status => {
                setCamPermission(status.state);
                camPerm = status;
                status.onchange = () => setCamPermission(status.state);
            })
            .catch(() => {
                setCamPermission(null);
            });

        // Cleanup listeners on unmount
        return () => {
            if (micPerm) {
                micPerm.onchange = null;
            }
            if (camPerm) {
                camPerm.onchange = null;
            }
        };
    }, []);

    let permissionsGranted;

    if (micPermission === 'granted' && camPermission === 'granted') {
        permissionsGranted = 'prejoin.lookGood';
    } else if (micPermission === 'denied' && camPermission === 'granted') {
        permissionsGranted = 'prejoin.camPermission';
    } else if (micPermission === 'granted' && camPermission === 'denied') {
        permissionsGranted = 'prejoin.micPermission';
    } else {
        permissionsGranted = false;
    }


    const { src, className } = permissionsGranted === false ? iconMap[deviceStatusType] : iconMap.ok;

    return (
        <div className = { `prejoin-preview-status ${interfaceConfig?.applyMeetingSettings === true && !matched ? `${className} bottom` : className}` }>
            <span className = 'prejoin-preview-error-desc'>
                <Icon
                    className = 'prejoin-preview-icon'
                    size = { 16 }
                    src = { src } />{permissionsGranted === false ? rawError ? `${t(deviceStatusText)} ${rawError}` : t(deviceStatusText) : t(permissionsGranted)}
            </span>

        </div>
    );
}


/**
 * Maps (parts of) the redux state to the React {@code Component} props.
 *
 * @param {Object} state - The redux state.
 * @returns {{ deviceStatusText: string, deviceStatusText: string }}
 */
function mapStateToProps(state) {

    return {
        deviceStatusText: getDeviceStatusText(state),
        deviceStatusType: getDeviceStatusType(state),
        rawError: getRawError(state)
    };
}

export default translate(connect(mapStateToProps)(DeviceStatus));
